import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Badge } from '@dopomogai/ui';
import { motion } from 'framer-motion';
import { Trophy, Zap, Layers, ArrowRight } from 'lucide-react';

export type TrackItem = {
  badgeKey: string;
  titleKey: string;
  textKey: string;
  resultKey: string;
  imageSrc: string;
  accentColor: 'emerald' | 'violet' | 'blue';
  actionId?: string; // ADDED: Specific action for this track
};

export type TracksSectionProps = {
  titleKey?: string;
  items: TrackItem[];
  onAction?: (actionId: string) => void;
};

export const TracksSection: React.FC<TracksSectionProps> = ({
  titleKey,
  items,
  onAction
}) => {
  const { t } = useTranslation('homepage');

  const handleCardClick = (item: TrackItem) => {
    if (onAction) {
        // CRITICAL FIX: Pass the specific actionId (e.g. TRACK_CREATOR) 
        // fallback to SCROLL_BOOKING only if undefined
        onAction(item.actionId || 'SCROLL_BOOKING');
    } else {
        const el = document.getElementById('booking');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="bg-slate-50 py-16 md:py-24 border-b-2 border-slate-200">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <Badge text="Use Cases" accentColor="slate" className="mb-4" />
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-4">
            {t(titleKey || 'tracks.title', 'Choose Your Transformation')}
          </h2>
          <p className="text-lg text-slate-600 font-medium">
            We configure the infrastructure differently based on your stage. Select your path to start the audit.
          </p>
        </div>

        {/* The Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {items.map((item, idx) => (
            <motion.div 
              key={idx}
              onClick={() => handleCardClick(item)} // Pass item to handler
              className="flex flex-col h-full bg-white border-2 border-black rounded-2xl overflow-hidden relative group cursor-pointer"
              whileHover={{ y: -8, boxShadow: "8px 8px 0px 0px rgba(17,24,39,1)" }}
              initial={{ boxShadow: "4px 4px 0px 0px rgba(17,24,39,0.1)" }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              
              {/* 1. Image Container */}
              <div className="h-56 w-full bg-slate-100 border-b-2 border-black relative overflow-hidden">
                 <img 
                   src={item.imageSrc} 
                   alt={t(item.titleKey)} 
                   className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                 />
                 
                 <div className="absolute top-4 left-4">
                  <span className={clsx(
                    "text-[10px] font-bold uppercase tracking-widest border border-black px-2 py-1 rounded-md bg-white shadow-sm",
                    item.accentColor === 'emerald' && "text-emerald-700",
                    item.accentColor === 'violet' && "text-violet-700",
                    item.accentColor === 'blue' && "text-blue-700",
                  )}>
                    {t(item.badgeKey)}
                  </span>
                 </div>
              </div>

              {/* 2. Content Body */}
              <div className="p-6 md:p-8 flex flex-col flex-grow">
                <h3 className="text-2xl font-extrabold text-slate-900 mb-3 leading-tight group-hover:text-emerald-600 transition-colors">
                  {t(item.titleKey)}
                </h3>
                
                <p className="text-slate-600 text-sm md:text-base leading-relaxed mb-6 flex-grow font-medium">
                  {t(item.textKey)}
                </p>

                {/* 3. Result Footer */}
                <div className="pt-4 border-t-2 border-slate-100 mt-auto">
                  <div className="flex items-center justify-between">
                      <div className="flex items-start gap-2">
                        <div className={clsx(
                        "mt-0.5 shrink-0",
                        item.accentColor === 'emerald' && "text-emerald-600",
                        item.accentColor === 'violet' && "text-violet-600",
                        item.accentColor === 'blue' && "text-blue-600",
                        )}>
                        {item.accentColor === 'emerald' && <Trophy size={16} />}
                        {item.accentColor === 'violet' && <Zap size={16} />}
                        {item.accentColor === 'blue' && <Layers size={16} />}
                        </div>
                        <p className="text-xs md:text-sm font-bold text-slate-800">
                        {t(item.resultKey)}
                        </p>
                    </div>
                    
                    <ArrowRight size={20} className="text-slate-900 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                  </div>
                </div>
              </div>

            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};